import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Switch } from "./ui/switch";

const KEY = "berglar-consent";

export const getConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
};

const saveConsent = (media) => {
  localStorage.setItem(KEY, JSON.stringify({ v: 1, necessary: true, media, ts: new Date().toISOString() }));
  window.dispatchEvent(new CustomEvent("berglar:consent-changed", { detail: { media } }));
};

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const [media, setMedia] = useState(() => getConsent()?.media === true);

  useEffect(() => {
    if (!getConsent()) {
      const t = setTimeout(() => setOpen(true), 1400);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const onOpen = () => {
      setMedia(getConsent()?.media === true);
      setSettings(true);
      setOpen(true);
    };
    window.addEventListener("berglar:open-consent", onOpen);
    return () => window.removeEventListener("berglar:open-consent", onOpen);
  }, []);

  const close = (mediaValue) => {
    saveConsent(mediaValue);
    setOpen(false);
    setSettings(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 32 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          role="dialog"
          aria-label="Datenschutz-Einstellungen"
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-[420px] z-[80]"
        >
          <div className="relative bg-white border border-line rounded-2xl shadow-[0_18px_60px_-18px_rgba(30,39,28,0.45)] overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gold" aria-hidden="true" />
            {getConsent() && (
              <button
                onClick={() => setOpen(false)}
                aria-label="Schließen"
                data-testid="cookie-close-button"
                className="absolute top-3.5 right-3.5 p-2 text-muted hover:text-ink transition-colors"
              >
                <X size={16} />
              </button>
            )}
            <div className="p-6 md:p-7">
              <div className="flex items-center gap-2.5">
                <Cookie size={18} className="text-golddark" strokeWidth={1.8} />
                <p className="font-display text-lg font-semibold text-ink tracking-tight">Datenschutz-Einstellungen</p>
              </div>
              <p className="text-muted text-sm leading-relaxed mt-3">
                Diese Website nutzt <span className="text-ink font-medium">keine Tracking- oder Werbe-Cookies</span> –
                nur technisch Notwendiges. Einzig Google Maps (Anfahrtskarte) lädt erst mit Ihrer Einwilligung.
              </p>

              {settings && (
                <div className="mt-5 flex flex-col gap-4 border-t border-line pt-5" data-testid="cookie-settings-panel">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-ink">Technisch notwendig</p>
                      <p className="text-xs text-muted leading-relaxed mt-1">
                        Sicherheits-Cookie des Hosters, Chat-Sitzung, Speicherung dieser Auswahl.
                      </p>
                    </div>
                    <Switch checked disabled aria-label="Technisch notwendig – immer aktiv" data-testid="cookie-switch-necessary" />
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-ink">Externe Medien (Google Maps)</p>
                      <p className="text-xs text-muted leading-relaxed mt-1">
                        Anfahrtskarte auf der Kontaktseite. Dabei werden Daten an Google übertragen.
                      </p>
                    </div>
                    <Switch checked={media} onCheckedChange={setMedia} aria-label="Externe Medien erlauben" data-testid="cookie-switch-media" />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3 mt-6">
                {settings ? (
                  <>
                    <button onClick={() => close(media)} data-testid="cookie-save-button" className="btn-primary !px-5 !py-3 text-sm">
                      Auswahl speichern
                    </button>
                    <button
                      onClick={() => close(true)}
                      data-testid="cookie-accept-all-button"
                      className="px-5 py-3 text-sm font-semibold border border-line rounded-full text-ink hover:border-gold hover:text-golddark transition-colors duration-300"
                    >
                      Alle akzeptieren
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => close(true)} data-testid="cookie-accept-all-button" className="btn-primary !px-5 !py-3 text-sm">
                      Alle akzeptieren
                    </button>
                    <button
                      onClick={() => close(false)}
                      data-testid="cookie-necessary-only-button"
                      className="px-5 py-3 text-sm font-semibold border border-line rounded-full text-ink hover:border-gold hover:text-golddark transition-colors duration-300"
                    >
                      Nur notwendige
                    </button>
                    <button
                      onClick={() => setSettings(true)}
                      data-testid="cookie-settings-button"
                      className="px-3 py-3 text-sm text-muted underline underline-offset-4 hover:text-golddark transition-colors duration-300"
                    >
                      Einstellungen
                    </button>
                  </>
                )}
              </div>
              <p className="text-[11px] text-muted mt-4">
                Details in der{" "}
                <Link to="/datenschutz" data-testid="cookie-privacy-link" className="underline underline-offset-2 hover:text-golddark transition-colors">
                  Datenschutzerklärung
                </Link>{" "}
                ·{" "}
                <Link to="/impressum" data-testid="cookie-imprint-link" className="underline underline-offset-2 hover:text-golddark transition-colors">
                  Impressum
                </Link>{" "}
                · Widerruf jederzeit über „Cookie-Einstellungen“ im Seitenfuß.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
